import { type ComponentPropsWithoutRef, type MouseEvent } from 'react'
import { ArrowUpRight, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import type { Components, ExtraProps } from 'streamdown'

import { getMainRouteConfigForLocation } from '@/config/mainRoutes'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { mergeShellSearch } from '@/lib/shellSearch'
import { cn } from '@/lib/utils'
import { ChatMarkdownImage } from './ChatMarkdownImage'
import { ChatMarkdownTable } from './ChatMarkdownTable'

const LINK_CLASS = 'wrap-anywhere font-medium text-accent-solid underline'
// Glyph scales with the link's font size (size-[1em]) so it never looks
// undersized next to larger text; align-[-0.125em] seats it on the baseline.
const GLYPH_CLASS = 'ml-0.5 inline-block size-[1em] align-[-0.125em]'

const buildHref = (url: URL, currentSearch: string): string =>
  url.pathname + mergeShellSearch(url.search, currentSearch) + url.hash

/**
 * Anchor renderer for David's chat markdown. Internal links navigate in-app
 * (SPA) on a plain click while staying real anchors (copy / cmd- / middle-click
 * open a new tab natively), marked with a ↗ (ArrowUpRight) "opens in-app"
 * glyph; external links open a new browser tab with a box ExternalLink glyph.
 * See the david-shared-state plan, Step 4.
 */
export function ChatMarkdownLink({
  href,
  children,
  node: _node,
  className,
  ...rest
}: ComponentPropsWithoutRef<'a'> & ExtraProps) {
  const { toTask } = useAppNavigate()

  let url: URL | null = null
  if (href) {
    try {
      url = new URL(href, window.location.origin)
    } catch {
      url = null
    }
  }

  // Bail to inert text when there's no href or it won't parse.
  if (!href || !url) {
    return <span className={cn(className)}>{children}</span>
  }

  // Scheme guard: strip anything that isn't http(s) (defense-in-depth vs
  // javascript:/data:). Render the text, not a clickable link.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return <span className={cn(LINK_CLASS, className)}>{children}</span>
  }

  const isInternal = url.origin === window.location.origin

  if (!isInternal) {
    return (
      <a
        {...rest}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(LINK_CLASS, className)}>
        {children}
        <ExternalLink className={GLYPH_CLASS} aria-hidden />
      </a>
    )
  }

  // Real href carries merged shell context so native new-tab keeps the
  // conversation; onClick handles the plain-click SPA navigation.
  const href_ = buildHref(url, window.location.search)

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Let the browser handle new-tab/window intents natively.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
      return
    e.preventDefault()
    // Unresolved internal path (e.g. a stale/typo link): the app has no 404, so
    // toast instead of navigating into a blank panel. `/` is Home (valid).
    if (
      url.pathname !== '/' &&
      getMainRouteConfigForLocation({ pathname: url.pathname }) === null
    ) {
      toast('This link may be outdated')
      return
    }
    toTask(url.pathname + url.search + url.hash)
  }

  return (
    <a
      {...rest}
      href={href_}
      onClick={onClick}
      className={cn(LINK_CLASS, className)}>
      {children}
      <ArrowUpRight className={GLYPH_CLASS} aria-hidden />
    </a>
  )
}

// Module constants: MessageResponse's memo only diffs `children`, so the
// components maps must be stable across renders or they would be dropped.
// Two maps because the concerns ride different switches: link interception is
// David-only (Hanna keeps default link behavior), while the table toolbar and
// image frame apply to every agent.
export const CHAT_MARKDOWN_COMPONENTS: Components = {
  a: ChatMarkdownLink,
  img: ChatMarkdownImage,
  table: ChatMarkdownTable,
}

export const CHAT_MARKDOWN_TABLE_ONLY: Components = {
  img: ChatMarkdownImage,
  table: ChatMarkdownTable,
}
